/**
 * @fileoverview Connect Intake workspaces to distinct backend read and action boundaries.
 *
 * Overview, pipeline, workflow, documents, decisions, outreach, and communications retain separate
 * contracts. Callers own screen state and refresh coordination; most helpers
 * preserve Axios response promises. Explicit mutations retain backend identity
 * and admission rules rather than hiding business actions in a read.
 *
 * `kc-internal-backend/routes/intake/__init__.py` owns HTTP handlers;
 * `kc-internal-backend/services/intake/projections.py` owns read models.
 * Convenience wrappers must preserve those boundaries so mocks and screens do not
 * collapse independently owned facts or treat an available action as permission.
 */

import api from '../../api'



export function fetchIntakeOptions() {
    return api.get('/intake/options')
}



export function fetchIntakeLeads(params) {
    return api.get('/intake/leads', { params })
}



export function fetchIntakeLeadOverview(leadId) {
    return api.get(`/intake/leads/${leadId}`)
}



export function fetchIntakeLeadPreview(leadId) {
    return api.get(`/intake/leads/${leadId}/preview`)
}



export function fetchIntakeLeadPipeline(leadId) {
    return api.get(`/intake/leads/${leadId}/pipeline`)
}



export function fetchIntakeLeadWorkflow(leadId) {
    return api.get(`/intake/leads/${leadId}/workflow`)
}



export function fetchIntakeLeadRuns(leadId, params) {
    return api.get(`/intake/leads/${leadId}/runs`, { params })
}



export function updateIntakeLead(leadId, payload) {
    return api.patch(`/intake/leads/${leadId}`, payload)
}



export function reingestIntakeLeadFromPipedrive(leadId) {
    return api.post(`/intake/leads/${leadId}/pipedrive/reingest`)
}



export function resetIntakeLeadPipeline(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/reset-pipeline`, payload)
}



export function overrideIntakeEngagementPackage(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/engagement-package/override`, payload)
}



export function fetchIntakeAttorneyReviewHandoff(leadId) {
    return api.get(`/intake/leads/${leadId}/attorney-review-handoff`)
}



export function confirmIntakeAttorneyReviewHandoff(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/attorney-review-handoff`, payload)
}



export function fetchIntakeLeadEvents(leadId, params) {
    return api.get(`/intake/leads/${leadId}/events`, { params })
}



export function fetchIntakeLeadTimeline(leadId, params) {
    return api.get(`/intake/leads/${leadId}/timeline`, { params })
}



export function fetchIntakeOutreach(leadId) {
    return api.get(`/intake/leads/${leadId}/outreach`)
}

export function fetchIntakeCommunications(params) {
    return api.get('/intake/communications', { params })
}

export function fetchIntakeCommunication(leadId) {
    return api.get(`/intake/leads/${leadId}/communications`)
}

export function reviewIntakeCommunication(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/communications/review`, payload)
}



export function createIntakeOutreach(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/outreach`, payload)
}



export function updateIntakeOutreach(leadId, messageId, payload) {
    return api.patch(`/intake/leads/${leadId}/outreach/${messageId}`, payload)
}



export function sendIntakeOutreach(leadId, messageId, payload = {}) {
    return api.post(`/intake/leads/${leadId}/outreach/${messageId}/send`, payload)
}



export function fetchIntakeDocuments(leadId) {
    // Local and staging review surfaces use the already-projected document rows.
    // A page load must never trigger a live Google Drive reconciliation.
    return api.get(`/intake/leads/${leadId}/documents`, { params: { refresh_drive: false } })
}



export function fetchIntakeEngagementDraft(leadId) {
    return api.get(`/intake/leads/${leadId}/engagement-draft`)
}



export function requestIntakeEngagementAutofill(leadId, payload = {}) {
    return api.post(`/intake/leads/${leadId}/engagement-draft/autofill`, payload)
}



export function generateIntakeEngagementDraft(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/engagement-draft/generate`, payload)
}



export function approveIntakeEngagementDraft(leadId) {
    return api.post(`/intake/leads/${leadId}/engagement-draft/approve`)
}



export function uploadIntakeDocuments(leadId, formData) {
    return api.post(`/intake/leads/${leadId}/documents/upload`, formData)
}



export function updateIntakeDocument(leadId, documentId, payload) {
    return api.patch(`/intake/leads/${leadId}/documents/${documentId}`, payload)
}



export function fetchIntakeDecisions(leadId) {
    return api.get(`/intake/leads/${leadId}/decision`)
}



export function createIntakeDecision(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/decision`, payload)
}



export function fetchIntakeAttorneyReviews(params) {
    return api.get('/intake/attorney-reviews', { params })
}

/**
 * Fetch one assigned-review page and return its numeric total count, defaulting to zero.
 */
export async function fetchAssignedIntakeAttorneyReviewCount() {
    const response = await fetchIntakeAttorneyReviews({ view: 'assigned', page: 1, page_size: 1 })
    return Number(response.data?.total || 0)
}



export function fetchIntakeAttorneyReview(reviewId) {
    return api.get(`/intake/attorney-reviews/${reviewId}`)
}



export function fetchIntakePromotionBatches(params) {
    return api.get('/intake/promotion-batches', { params })
}



export function fetchIntakePromotionBatch(batchId, params) {
    return api.get(`/intake/promotion-batches/${batchId}`, { params })
}



export function fetchIntakeAutomationJobs() {
    return api.get('/automations/jobs')
}



export function createIntakeAttorneyReviews(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/attorney-reviews`, payload)
}



export function submitIntakeAttorneyReviewDecision(reviewId, payload) {
    return api.post(`/intake/attorney-reviews/${reviewId}/decision`, payload)
}



export function fetchIntakeSourceEvents(leadId, params) {
    return api.get(`/intake/leads/${leadId}/source-events`, { params })
}



export function fetchIntakePipedriveNotes(leadId, params) {
    return api.get(`/intake/leads/${leadId}/pipedrive-notes`, { params })
}



export function fetchIntakeDuplicateCandidates(leadId, params) {
    return api.get(`/intake/leads/${leadId}/duplicate-candidates`, { params })
}



export function runDuplicateCandidateAgentReview(leadId, candidateId, payload) {
    return api.post(`/intake/leads/${leadId}/duplicate-candidates/${candidateId}/agent-review`, payload)
}



export function mergeIntakeDuplicateCandidate(leadId, candidateId, payload) {
    return api.post(`/intake/leads/${leadId}/duplicate-candidates/${candidateId}/merge`, payload)
}



export function resolveIntakeDuplicateCandidate(leadId, candidateId, payload) {
    return api.post(`/intake/leads/${leadId}/duplicate-candidates/${candidateId}/resolve`, payload)
}



export function fetchIntakeAgentRunDetail(leadId, runId) {
    return api.get(`/intake/leads/${leadId}/agent-runs/${runId}`)
}



export function fetchIntakeAgentRunFeedback(leadId, runId) {
    return api.get(`/intake/leads/${leadId}/agent-runs/${runId}/feedback`)
}



export function createIntakeAgentRunFeedback(leadId, runId, payload) {
    return api.post(`/intake/leads/${leadId}/agent-runs/${runId}/feedback`, payload)
}



export function updateIntakeFeedback(feedbackId, payload) {
    return api.patch(`/intake/feedback/${feedbackId}`, payload)
}



export function fetchIntakeAgents() {
    return api.get('/intake/agents')
}



export function updateIntakeAgent(agentId, payload) {
    return api.patch(`/intake/agents/${agentId}`, payload)
}



export function createIntakeAgent(payload) {
    return api.post('/intake/agents', payload)
}



export function fetchIntakePromptVersions(agentId) {
    return api.get(`/intake/agents/${agentId}/prompt-versions`)
}



export function createIntakePromptVersion(agentId, payload) {
    return api.post(`/intake/agents/${agentId}/prompt-versions`, payload)
}



export function updateIntakePromptVersion(versionId, payload) {
    return api.patch(`/intake/prompt-versions/${versionId}`, payload)
}



export function publishIntakePromptVersion(versionId, payload) {
    return api.post(`/intake/prompt-versions/${versionId}/publish`, payload)
}



export function archiveIntakePromptVersion(versionId) {
    return api.post(`/intake/prompt-versions/${versionId}/archive`)
}



export function fetchIntakeGlobalPrompts() {
    return api.get('/intake/global-prompts')
}



export function fetchIntakeGlobalPromptVersions(promptKey) {
    return api.get(`/intake/global-prompts/${promptKey}/versions`)
}



export function createIntakeGlobalPromptVersion(promptKey, payload) {
    return api.post(`/intake/global-prompts/${promptKey}/versions`, payload)
}



export function updateIntakeGlobalPromptVersion(versionId, payload) {
    return api.patch(`/intake/global-prompt-versions/${versionId}`, payload)
}



export function publishIntakeGlobalPromptVersion(versionId, payload) {
    return api.post(`/intake/global-prompt-versions/${versionId}/publish`, payload)
}



export function archiveIntakeGlobalPromptVersion(versionId) {
    return api.post(`/intake/global-prompt-versions/${versionId}/archive`)
}



export function fetchIntakePipelineVersions() {
    return api.get('/intake/pipeline-versions')
}



export function fetchIntakePipelineVersion(versionId) {
    return api.get(`/intake/pipeline-versions/${versionId}`)
}



export function createIntakePipelineVersion(payload) {
    return api.post('/intake/pipeline-versions', payload)
}



export function updateIntakePipelineVersion(versionId, payload) {
    return api.patch(`/intake/pipeline-versions/${versionId}`, payload)
}



export function publishIntakePipelineVersion(versionId, payload) {
    return api.post(`/intake/pipeline-versions/${versionId}/publish`, payload)
}



export function archiveIntakePipelineVersion(versionId) {
    return api.post(`/intake/pipeline-versions/${versionId}/archive`)
}



export function updateLeadPipelineVersion(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/pipeline-version`, payload)
}



export function continueInformationEvaluationReview(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/information-evaluation/continue`, payload)
}



export function resolveIntakeStaffReview(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/staff-review/resolve`, payload)
}



export function runIntakeStage(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/run-stage`, payload)
}



export function runIntakePipeline(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/run-pipeline`, payload)
}



export function skipIntakeStage(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/skip-stage`, payload)
}



export function runIntakeAgent(leadId, payload) {
    return api.post(`/intake/leads/${leadId}/run-agent`, payload)
}



export function fetchIntakeModelProfiles() {
    return api.get('/intake/model-profiles')
}



export function createIntakeModelProfile(payload) {
    return api.post('/intake/model-profiles', payload)
}



export function updateIntakeModelProfile(profileId, payload) {
    return api.patch(`/intake/model-profiles/${profileId}`, payload)
}



export function fetchIntakeStaffRoles() {
    return api.get('/intake/staff-roles')
}



export function createIntakeStaffRole(payload) {
    return api.post('/intake/staff-roles', payload)
}



export function updateIntakeStaffRole(roleId, payload) {
    return api.patch(`/intake/staff-roles/${roleId}`, payload)
}



export function createIntakeStaffRoleAssignment(payload) {
    return api.post('/intake/staff-role-assignments', payload)
}



export function updateIntakeStaffRoleAssignment(assignmentId, payload) {
    return api.patch(`/intake/staff-role-assignments/${assignmentId}`, payload)
}



export function fetchIntakeAdminMetrics() {
    return api.get('/intake/admin/metrics')
}



export function fetchIntakeOperationalSettings() {
    return api.get('/intake/admin/operational-settings')
}



export function updateIntakeOperationalSettings(payload) {
    return api.patch('/intake/admin/operational-settings', payload)
}



export function fetchIntakeAttorneyRoutingProfiles() {
    return api.get('/intake/admin/attorney-routing-profiles')
}



export function updateIntakeAttorneyRoutingProfile(attorneyId, payload) {
    return api.patch(`/intake/admin/attorney-routing-profiles/${attorneyId}`, payload)
}
